import { baseAPI } from '@/api/axios';
import { JOBS } from '@/api/endpoints';
import showAPIErrorToast from '@/utils/showAPIErrorToast';

const handleErrorResponse = (err, dispatch, disableRequestIndicator = false) => {
    if (!disableRequestIndicator) {
        showAPIErrorToast(err);
        dispatch('SET_API_RESPONSE_STATUS', 'error');
    }
};

const initialState = {
    jobs: [],
    isFetchingJobs: false,
    job: null,
    jobTalents: [], // talents of current `job`
    shouldRefetchJobTalents: false, // when talent list is changed from outside
    apiResponseStatus: '',
    smartJob: {
        _id: '',
        job_url: '',
        job_description: '',
        job_title: '',
        team_name: '',
        filters: [],
        custom_message: '',
    },
    jobErrors: {
        review_job_details_error: false,
        custom_message_error: false,
        filters: {
            skills_tag_error: [],
            years_of_experience_error: false,
        },
    },
    promoteJob: {
        _id: '',
        title: '',
        filters: [],
    },
};

const getters = {
    jobs: (state) => {
        return state.jobs;
    },
    isFetchingJobs: (state) => {
        return state.isFetchingJobs;
    },
    job: (state) => {
        return state.job;
    },
    apiResponseStatus: ({ apiResponseStatus }) => {
        return apiResponseStatus;
    },
    jobTalents: (state) => {
        return state.jobTalents;
    },
    smartJob: (state) => {
        return state.smartJob;
    },
    smartJobFilterErrors: (state) => {
        const error = state.jobErrors;
        return error.filters.skills_tag_error.length > 0 || error.filters.years_of_experience_error;
    },
    smartJobError: (state) => {
        const error = state.jobErrors;
        return (
            error.review_job_details_error ||
            error.custom_message_error ||
            error.filters.skills_tag_error.length > 0 ||
            error.filters.years_of_experience_error
        );
    },
    shouldRefetchJobTalents: (state) => {
        return state.shouldRefetchJobTalents;
    },
    promoteJob: (state) => {
        return state.promoteJob;
    },
    promoteJobError: (state) => {
        return state.jobErrors.filters.skills_tag_error.length > 0 || state.jobErrors.filters.years_of_experience_error;
    },
};

const mutations = {
    SET_JOBS(state, jobs) {
        state.jobs = jobs;
    },
    SET_IS_FETCHING_JOBS(state, value) {
        state.isFetchingJobs = value;
    },
    SET_JOB(state, job) {
        state.job = job;
    },
    SET_API_RESPONSE_STATUS(state, apiResponseStatus) {
        state.apiResponseStatus = apiResponseStatus;
    },
    SET_JOB_TALENTS(state, talents) {
        const { jobTalents = [] } = state;
        state.jobTalents = [...jobTalents, ...talents];
    },
    RESET_JOB_TALENTS(state) {
        state.jobTalents = [];
    },
    RESET_JOB(state) {
        state.job = null;
    },
    SET_SMART_JOB(state, job) {
        state.smartJob = job;
    },
    UPDATE_SMART_JOB_TITLE(state, title) {
        state.smartJob.job_title = title;
    },
    SET_SMART_JOB_TEAM_NAME(state, teamName) {
        state.smartJob.team_name = teamName;
    },
    UPDATE_SMART_JOB_FILTERS(state, filters) {
        state.smartJob.filters = filters;
    },
    SET_SMART_JOB_CUSTOM_MESSAGE(state, message) {
        state.smartJob.custom_message = message;
    },
    SET_REVIEW_JOB_DETAILS_ERROR(state, value) {
        state.jobErrors.review_job_details_error = value;
    },
    SET_CUSTOM_MESSAGE_ERROR(state, value) {
        state.jobErrors.custom_message_error = value;
    },
    SET_SKILLS_TAG_ERROR(state, { skillId, errorState }) {
        if (errorState) {
            if (!state.jobErrors.filters.skills_tag_error.includes(skillId)) {
                state.jobErrors.filters.skills_tag_error.push(skillId);
            }
        } else {
            const index = state.jobErrors.filters.skills_tag_error.indexOf(skillId);
            if (index > -1) {
                state.jobErrors.filters.skills_tag_error.splice(index, 1);
            }
        }
    },
    SET_YEARS_OF_EXPERIENCE_ERROR(state, value) {
        state.jobErrors.filters.years_of_experience_error = value;
    },
    RESET_SKILLS_TAG_ERRORS(state) {
        state.jobErrors.filters.skills_tag_error = [];
    },
    RESET_JOB_ERRORS(state) {
        state.jobErrors = {
            review_job_details_error: false,
            custom_message_error: false,
            filters: {
                skills_tag_error: [],
                years_of_experience_error: false,
            },
        };
    },
    SET_SHOULD_REFETCH_JOB_TALENTS(state, value) {
        state.shouldRefetchJobTalents = value;
    },
    SET_PROMOTE_JOB_ID(state, id) {
        state.promoteJob._id = id;
    },
    SET_PROMOTE_JOB_TITLE(state, title) {
        state.promoteJob.title = title;
    },
    SET_PROMOTE_JOB_FILTERS(state, filters) {
        state.promoteJob.filters = filters;
    },
    RESET_PROMOTE_JOB_FILTERS(state) {
        state.promoteJob.filters = [];
    },
};

const actions = {
    async FETCH_JOBS({ dispatch, rootState }) {
        dispatch('SET_API_RESPONSE_STATUS', 'pending');
        baseAPI
            .get(`${JOBS.ROOT()}?createdBy=${rootState.app.user._id}`)
            .then(({ data }) => {
                dispatch('SET_JOBS', data);
                dispatch('SET_API_RESPONSE_STATUS', 'success');
            })
            .catch((err) => {
                handleErrorResponse(err, dispatch);
            });
    },
    async FETCH_JOBS_BY_ACCOUNT_ID({ commit, dispatch, rootState }) {
        dispatch('SET_API_RESPONSE_STATUS', 'pending');
        commit('SET_IS_FETCHING_JOBS', true);
        baseAPI
            .get(JOBS.GET_BY_ACCOUNT_ID(rootState.app.user.account_id))
            .then(({ data }) => {
                dispatch('SET_JOBS', data);
                dispatch('SET_API_RESPONSE_STATUS', 'success');
            })
            .catch((err) => {
                handleErrorResponse(err, dispatch);
            })
            .finally(() => {
                commit('SET_IS_FETCHING_JOBS', false);
            });
    },
    async FETCH_JOB({ dispatch }, jobId) {
        dispatch('SET_API_RESPONSE_STATUS', 'pending');
        await baseAPI
            .get(JOBS.DETAILS(jobId))
            .then(({ data }) => {
                dispatch('SET_JOB', data);
                dispatch('SET_API_RESPONSE_STATUS', 'success');
            })
            .catch((err) => {
                handleErrorResponse(err, dispatch);
            });
    },
    SET_JOBS({ commit }, jobs) {
        commit('SET_JOBS', jobs);
    },
    SET_JOB({ commit }, job) {
        commit('SET_JOB', job);
    },
    SET_API_RESPONSE_STATUS({ commit }, apiResponseStatus) {
        commit('SET_API_RESPONSE_STATUS', apiResponseStatus);
    },
    SET_JOB_TALENTS({ commit }, talents) {
        commit('SET_JOB_TALENTS', talents);
    },
    RESET_JOB_TALENTS({ commit }) {
        commit('RESET_JOB_TALENTS');
    },
    RESET_JOB({ commit }) {
        commit('RESET_JOB');
        commit('RESET_JOB_TALENTS');
    },
    SET_SMART_JOB({ commit }, job) {
        commit('SET_SMART_JOB', job);
    },
    RESET_SMART_JOB({ commit }) {
        commit('SET_SMART_JOB', initialState.smartJob);
    },
    UPDATE_SMART_JOB_TITLE({ commit }, title) {
        commit('UPDATE_SMART_JOB_TITLE', title);
    },
    SET_SMART_JOB_TEAM_NAME({ commit }, teamName) {
        commit('SET_SMART_JOB_TEAM_NAME', teamName);
    },
    UPDATE_SMART_JOB_FILTERS({ commit }, filters) {
        commit('UPDATE_SMART_JOB_FILTERS', filters);
    },
    SET_SMART_JOB_CUSTOM_MESSAGE({ commit }, message) {
        commit('SET_SMART_JOB_CUSTOM_MESSAGE', message);
    },
    SET_REVIEW_JOB_DETAILS_ERROR({ commit }, value) {
        commit('SET_REVIEW_JOB_DETAILS_ERROR', value);
    },
    SET_CUSTOM_MESSAGE_ERROR({ commit }, value) {
        commit('SET_CUSTOM_MESSAGE_ERROR', value);
    },
    SET_SKILLS_TAG_ERROR({ commit }, payload) {
        commit('SET_SKILLS_TAG_ERROR', payload);
    },
    SET_YEARS_OF_EXPERIENCE_ERROR({ commit }, value) {
        commit('SET_YEARS_OF_EXPERIENCE_ERROR', value);
    },
    RESET_SKILLS_TAG_ERRORS({ commit }) {
        commit('RESET_SKILLS_TAG_ERRORS');
    },
    RESET_SMART_JOB_ERRORS({ commit }) {
        commit('RESET_JOB_ERRORS');
    },
    SET_SHOULD_REFETCH_JOB_TALENTS({ commit }, value) {
        commit('SET_SHOULD_REFETCH_JOB_TALENTS', value);
    },
    SET_PROMOTE_JOB_ID({ commit }, value) {
        commit('SET_PROMOTE_JOB_ID', value);
    },
    SET_PROMOTE_JOB_TITLE({ commit }, value) {
        commit('SET_PROMOTE_JOB_TITLE', value);
    },
    SET_PROMOTE_JOB_FILTERS({ commit }, value) {
        commit('SET_PROMOTE_JOB_FILTERS', value);
    },
    RESET_PROMOTE_JOB_FILTERS({ commit }) {
        commit('RESET_PROMOTE_JOB_FILTERS');
    },
    RESET_PROMOTE_JOB_ERRORS({ commit }) {
        commit('RESET_JOB_ERRORS');
    },
};

export default {
    namespaced: true,
    state: initialState,
    getters,
    mutations,
    actions,
};
